import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Alert,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Business as CompanyIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Notifications as NotificationIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Wifi as WifiIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
import { api } from '../services/api';
import { CURRENCIES } from '../utils/constants';
import LoadingPage from '../components/common/LoadingPage';
import { useNotification } from '../components/common/GlobalNotifications';
import PhoneInput from '../components/common/PhoneInput';
import CompanyNetworksTab from '../components/settings/CompanyNetworksTab';
import OperatingSchedulesTab from '../components/settings/OperatingSchedulesTab';

// Hook mejorado para cargar datos geográficos con estructura correcta
const useGeographicData = () => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [phoneCodes, setPhoneCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('Loading geographic data...');

        const [countriesRes, statesRes, citiesRes, phoneCodesRes] = await Promise.all([
          fetch('/countries-master/json/countries.json').catch(e => {
            console.error(t('hardcoded.pages_settingspage.error_loading_countries'), e);
            return { ok: false };
          }),
          fetch('/countries-master/json/states.json').catch(e => {
            console.error(t('hardcoded.pages_settingspage.error_loading_states'), e);
            return { ok: false };
          }),
          fetch('/countries-master/json/cities.json').catch(e => {
            console.error(t('hardcoded.pages_settingspage.error_loading_cities'), e);
            return { ok: false };
          }),
          fetch('/countries-master/json/codcountries.json').catch(e => {
            console.error(t('hardcoded.pages_settingspage.error_loading_phone_codes'), e);
            return { ok: false };
          })
        ]);

        let countriesData = [];
        let statesData = [];
        let citiesData = [];
        let phoneCodesData = [];

        if (countriesRes.ok) {
          const countriesJson = await countriesRes.json();
          countriesData = countriesJson.countries || [];
          console.log(t('hardcoded.pages_settingspage.countries_loaded'), countriesData.length);
        }

        if (statesRes.ok) {
          const statesJson = await statesRes.json();
          statesData = statesJson.states || [];
          console.log(t('hardcoded.pages_settingspage.states_loaded'), statesData.length);
        }

        if (citiesRes.ok) {
          const citiesJson = await citiesRes.json();
          citiesData = citiesJson.cities || [];
          console.log(t('hardcoded.pages_settingspage.cities_loaded'), citiesData.length);
        }

        if (phoneCodesRes.ok) {
          phoneCodesData = await phoneCodesRes.json();
          console.log(t('hardcoded.pages_settingspage.phone_codes_loaded'), phoneCodesData.length);
        }

        setCountries(countriesData);
        setStates(statesData);
        setCities(citiesData);
        setPhoneCodes(Array.isArray(phoneCodesData) ? phoneCodesData : []);

      } catch (error) {
        console.error('Error loading geographic data:', error);
        setError(error.message);
        setCountries([]);
        setStates([]);
        setCities([]);
        setPhoneCodes([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return { countries, states, cities, phoneCodes, loading, error };
};

// Componente mejorado para el selector de país con búsqueda
const CountryAutocomplete = React.memo(({ value, onChange, countries, disabled = false }) => {
  const { t } = useTranslation();
  const safeCountries = Array.isArray(countries) ? countries : [];

  const selectedCountry = useMemo(() => {
    return safeCountries.find(country => country.name === value) || null;
  }, [value, safeCountries]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      value={selectedCountry}
      onChange={(event, newValue) => {
        onChange(newValue ? newValue.name : '');
      }}
      options={safeCountries}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('settings.company.country')}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {option.name}
        </Box>
      )}
      noOptionsText={t('settings.company.noCountriesFound')}
      loadingText={t('settings.company.loadingCountries')}
    />
  );
});

// Componente mejorado para el selector de estado con búsqueda
const StateAutocomplete = React.memo(({ value, onChange, selectedCountry, countries, states, disabled = false }) => {
  const { t } = useTranslation();
  const safeCountries = Array.isArray(countries) ? countries : [];
  const safeStates = Array.isArray(states) ? states : [];

  const availableStates = useMemo(() => {
    if (!selectedCountry) return [];
    const country = safeCountries.find(c => c.name === selectedCountry);
    if (!country) return [];
    return safeStates.filter(state => state.id_country === country.id);
  }, [selectedCountry, safeCountries, safeStates]);

  const selectedState = useMemo(() => {
    return availableStates.find(state => state.name === value) || null;
  }, [value, availableStates]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled || !selectedCountry}
      value={selectedState}
      onChange={(event, newValue) => {
        onChange(newValue ? newValue.name : '');
      }}
      options={availableStates}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('settings.company.state')}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {option.name}
        </Box>
      )}
      noOptionsText={selectedCountry ? t('settings.company.noStatesFound') : t('settings.company.selectCountryFirst')}
      loadingText={t('settings.company.loadingStates')}
    />
  );
});

// Componente CORREGIDO para el selector de ciudad
const CityAutocomplete = React.memo(({ value, onChange, selectedState, states, cities, disabled = false }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const safeStates = Array.isArray(states) ? states : [];
  const safeCities = Array.isArray(cities) ? cities : [];

  const availableCities = useMemo(() => {
    if (!selectedState) return [];
    const state = safeStates.find(s => s.name === selectedState);
    if (!state) return [];

    let filteredCities = safeCities.filter(city => city.id_state === state.id);

    if (inputValue) {
      filteredCities = filteredCities.filter(city =>
        city.name.toLowerCase().includes(inputValue.toLowerCase())
      );
    }

    return filteredCities.slice(0, 100);
  }, [selectedState, safeStates, safeCities, inputValue]);

  // CORRIGIDO: Buscar ciudad en todas las ciudades del estado, no solo en las filtradas
  const selectedCity = useMemo(() => {
    if (!value || !selectedState) return null;

    const state = safeStates.find(s => s.name === selectedState);
    if (!state) return null;

    // Buscar en TODAS las ciudades del estado, no solo en las filtradas
    const allStateCities = safeCities.filter(city => city.id_state === state.id);
    const foundCity = allStateCities.find(city => city.name === value);

    console.log('Looking for city:', value, 'in state:', selectedState);
    console.log('Found city:', foundCity);
    console.log(t('hardcoded.pages_settingspage.available_cities_for_state'), allStateCities.length);

    return foundCity || null;
  }, [value, selectedState, safeStates, safeCities]);

  // NUEVO: Si no se encuentra la ciudad en los datos, permitir entrada libre
  const shouldShowFreeEntry = value && !selectedCity && selectedState;

  return (
    <Autocomplete
      fullWidth
      disabled={disabled || !selectedState}
      value={selectedCity}
      onChange={(event, newValue) => {
        onChange(newValue ? newValue.name : '');
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        // Si no hay ciudad seleccionada y el usuario está escribiendo, actualizar el valor
        if (!selectedCity && newInputValue) {
          onChange(newInputValue);
        }
      }}
      options={availableCities}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      freeSolo={shouldShowFreeEntry} // Permitir entrada libre si no se encuentra la ciudad
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('settings.company.city')}
          size="small"
          value={shouldShowFreeEntry ? value : params.inputProps.value}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          helperText={shouldShowFreeEntry ? t('hardcoded.pages_settingspage.ciudad_no_encontrada_en_la') : ''}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {option.name}
        </Box>
      )}
      noOptionsText={selectedState ? t('settings.company.noCitiesFound') : t('settings.company.selectStateFirst')}
      loadingText={t('settings.company.loadingCities')}
      filterOptions={(options) => options}
    />
  );
});

// Componente compacto para la información de la empresa
const CompanyInformationTab = React.memo(({
  companyForm,
  countries,
  states,
  cities,
  companyError,
  geoError,
  company,
  updateLoading,
  hasChanges,
  onFormChange,
  onSave,
  onReset,
  showDemoCleanup,
  cleanupLoading,
  onCleanupDemo
}) => {
  const { t } = useTranslation();

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 1 }}>
      {companyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {companyError.message}
        </Alert>
      )}

      {geoError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('hardcoded.pages_settingspage.error_cargando_datos_geograficos')}{geoError}{t("hardcoded.pages_settingspage.selector_warning")}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Fila 1: Información básica */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('settings.company.name')}
            size="small"
            value={companyForm.company_name || ''}
            onChange={(e) => onFormChange('company_name', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('settings.company.rfc')}
            size="small"
            value={company?.rfc || ''}
            disabled
            helperText={t('settings.company.rfcHelper')}
          />
        </Grid>

        {/* Fila 2: Gerente */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('settings.company.managerName')}
            size="small"
            value={companyForm.manager_name || ''}
            onChange={(e) => onFormChange('manager_name', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('settings.company.managerEmail')}
            type="email"
            size="small"
            value={companyForm.manager_email || ''}
            onChange={(e) => onFormChange('manager_email', e.target.value)}
          />
        </Grid>

        {/* Fila 3: Ubicación */}
        <Grid item xs={12} sm={4}>
          <CountryAutocomplete
            value={companyForm.country}
            onChange={(value) => onFormChange('country', value)}
            countries={countries}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <StateAutocomplete
            value={companyForm.state}
            onChange={(value) => onFormChange('state', value)}
            selectedCountry={companyForm.country}
            countries={countries}
            states={states}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <CityAutocomplete
            value={companyForm.city}
            onChange={(value) => onFormChange('city', value)}
            selectedState={companyForm.state}
            states={states}
            cities={cities}
          />
        </Grid>

        {/* Fila 4: Direcciones */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('settings.company.address1')}
            size="small"
            value={companyForm.address1 || ''}
            onChange={(e) => onFormChange('address1', e.target.value)}
            helperText={t('settings.company.address1Helper')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('settings.company.address2')}
            size="small"
            value={companyForm.address2 || ''}
            onChange={(e) => onFormChange('address2', e.target.value)}
            helperText={t('settings.company.address2Helper')}
          />
        </Grid>

        {/* Fila 5: Teléfono y Moneda */}
        <Grid item xs={12} md={6}>
          <PhoneInput
            value={companyForm.phone}
            onChange={(value) => onFormChange('phone', value)}
            label={t('settings.company.phone')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            value={CURRENCIES.find(c => c.code === companyForm.currency) || null}
            onChange={(event, newValue) => {
              onFormChange('currency', newValue ? newValue.code : '');
            }}
            options={CURRENCIES}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.code === value?.code}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('settings.company.currency')}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.code}>
                {option.label}
              </Box>
            )}
            noOptionsText={t('settings.company.noCurrenciesFound')}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          startIcon={updateLoading ? <CircularProgress size={14} /> : <SaveIcon fontSize="small" />}
          onClick={onSave}
          disabled={updateLoading || !hasChanges}
        >
          {t('settings.company.saveChanges')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onReset}
          disabled={updateLoading}
        >
          {t('settings.company.reset')}
        </Button>
      </Box>

      {showDemoCleanup && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t('hardcoded.pages_settingspage.estas_en_una_empresa_demo')}</Alert>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={onCleanupDemo}
            disabled={cleanupLoading}
            startIcon={cleanupLoading ? <CircularProgress size={14} /> : null}
          >
            {t('hardcoded.pages_settingspage.eliminar_todo_de_esta_empresa')}</Button>
        </Box>
      )}
    </Paper>
  );
});

const ServicePlanTab = React.memo(({ servicePlan, t }) => {
  const getStatusColor = (usage) => {
    if (usage >= 90) return 'error';
    if (usage >= 80) return 'warning';
    return 'success';
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 1 }}>
      {servicePlan ? (
        <Card>
          <CardContent sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom>
              {servicePlan.plan_name}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.servicePlan.monthlyCost')}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  ${servicePlan.cost}{t("hardcoded.pages_settingspage.per_month")}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.servicePlan.vehicleUsage')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">
                    {servicePlan.vehicle_usage}
                  </Typography>
                  <Chip
                    label={`${servicePlan.usage_percentage?.toFixed(1) || 0}%`}
                    color={getStatusColor(servicePlan.usage_percentage || 0)}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {servicePlan.description || t('hardcoded.pages_settingspage.sin_descripcion_disponible')}
            </Typography>
          </CardContent>
          <CardActions sx={{ pt: 0 }}>
            <Button size="small" color="primary" variant="contained">
              {t('settings.servicePlan.upgradePlan')}
            </Button>
          </CardActions>
        </Card>
      ) : (
        <Alert severity="info">
          {t('hardcoded.pages_settingspage.no_hay_informacion_del_plan')}</Alert>
      )}
    </Paper>
  );
});

// SecurityTab and NotificationsTab were removed per UX decision:
// notifications now live exclusively in Profile > Notifications and
// Company > Users > Edit/View user > Notifications.

// Componente principal
const SettingsPage = () => {
  const { user, hasPermission } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();
  const [activeTab, setActiveTab] = useState(0);
  const [companyForm, setCompanyForm] = useState({});
  const [originalCompanyData, setOriginalCompanyData] = useState({});
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const { t } = useTranslation();

  // Cargar datos geográficos
  const { countries, states, cities, loading: geoLoading, error: geoError } = useGeographicData();

  // API calls
  const {
    data: company,
    loading: companyLoading,
    error: companyError,
    refetch: refetchCompany
  } = useApiCall(api.companies.getCurrent);

  const {
    data: servicePlan,
    loading: planLoading
  } = useApiCall(api.companies.getServicePlan);

  const {
    mutate: updateCompany,
    loading: updateLoading
  } = useMutation(api.companies.updateCurrent, {
    onSuccess: () => {
      refetchCompany();
    }
  });

  // Función para extraer datos de la empresa
  const extractCompanyData = useCallback((companyData) => {
    if (!companyData) return {};

    let address1 = '';
    let address2 = '';
    if (companyData.address) {
      const addressParts = companyData.address.split('\n');
      address1 = addressParts[0] || '';
      address2 = addressParts.slice(1).join('\n') || '';
    }

    return {
      company_name: companyData.company_name || '',
      manager_name: companyData.manager_name || '',
      manager_email: companyData.manager_email || '',
      country: companyData.country || '',
      state: companyData.state || '',
      city: companyData.city || '',
      address1: address1,
      address2: address2,
      currency: companyData.currency || '',
      phone: companyData.phone || ''
    };
  }, []);

  // Inicializar formulario cuando se cargan los datos de la empresa
  useEffect(() => {
    if (company) {
      console.log(t('hardcoded.pages_settingspage.company_data_loaded'), company);
      const formData = extractCompanyData(company);
      setCompanyForm(formData);
      setOriginalCompanyData(formData);
    }
  }, [company, extractCompanyData]);

  // Detectar cambios en el formulario
  const hasChanges = useMemo(() => {
    if (!originalCompanyData || Object.keys(originalCompanyData).length === 0) {
      return false;
    }

    return Object.keys(companyForm).some(key =>
      companyForm[key] !== originalCompanyData[key]
    );
  }, [companyForm, originalCompanyData]);

  // Handlers
  const handleCompanyFormChange = useCallback((field, value) => {
    setCompanyForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset campos dependientes cuando cambia el país o estado
    if (field === 'country') {
      setCompanyForm(prev => ({
        ...prev,
        state: '',
        city: ''
      }));
    } else if (field === 'state') {
      setCompanyForm(prev => ({
        ...prev,
        city: ''
      }));
    }
  }, []);

  const handleSaveCompany = useCallback(async () => {
    try {
      const formData = { ...companyForm };

      // Combinar dirección
      const fullAddress = [formData.address1, formData.address2]
        .filter(part => part && part.trim())
        .join('\n');
      formData.address = fullAddress;

      // Remover campos temporales
      delete formData.address1;
      delete formData.address2;

      console.log('Saving company data:', formData);
      await updateCompany(formData);

      // Actualizar datos originales después de guardar exitosamente
      const newOriginalData = extractCompanyData({
        ...company,
        ...formData
      });
      setOriginalCompanyData(newOriginalData);

    } catch (error) {
      console.error('Error saving company data:', error);
    }
  }, [companyForm, updateCompany, company, extractCompanyData]);

  const handleResetForm = useCallback(() => {
    if (company) {
      const formData = extractCompanyData(company);
      setCompanyForm(formData);
    }
  }, [company, extractCompanyData]);

  const isArsenalUser = (user?.email?.toLowerCase?.() || '').endsWith(t('hardcoded.components_common_sidebar.arsenalmx'));
  const isDemoCompany = (company?.company_name || '').toLowerCase().includes(t('hardcoded.pages_settingspage.demo_arsenal'));

  const handleCleanupDemo = useCallback(async () => {
    if (!company?.id) {
      showWarning(t('hardcoded.pages_settingspage.no_se_encontro_la_empresa'));
      return;
    }

    if (!isArsenalUser) {
      showError(t('hardcoded.pages_settingspage.solo_usuarios_arsenalmx_pueden_limpiar'));
      return;
    }

    if (!isDemoCompany) {
      showWarning(t('hardcoded.pages_settingspage.esta_empresa_no_es_una'));
      return;
    }

    const confirmed = window.confirm(
      t('hardcoded.pages_settingspage.esta_accion_eliminara_absolutamente_todo')
    );

    if (!confirmed) {
      return;
    }

    try {
      setCleanupLoading(true);
      const response = await api.admin.cleanupDemo(company.id);

      if (response?.status === 'completed') {
        showSuccess(t('hardcoded.pages_settingspage.empresa_demo_eliminada_exitosamente'));
        window.location.reload();
      } else if (response?.status === 'warning') {
        showWarning(response?.message || t('hardcoded.pages_settingspage.no_se_encontro_demo_para'));
      } else {
        showError(response?.message || t('hardcoded.pages_settingspage.error_al_limpiar_empresa_demo'));
      }
    } catch (error) {
      const message = error?.response?.data?.detail || error?.message || t('hardcoded.pages_settingspage.error_al_limpiar_empresa_demo');
      showError(message);
    } finally {
      setCleanupLoading(false);
    }
  }, [company, isArsenalUser, isDemoCompany, showError, showSuccess, showWarning]);

  if (companyLoading || planLoading || geoLoading) {
    return <LoadingPage message={t('settings.loadingMessage')} />;
  }

  if (!hasPermission('manager')) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="error">
          {t('settings.noPermission')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      {/* Header compacto */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          {t('settings.title')}
        </Typography>
      </Box>

      {/* Tabs compactos */}
      <Paper sx={{ mb: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 64 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              py: { xs: 1, sm: 2 }
            }
          }}
        >
          <Tab
            icon={<CompanyIcon fontSize="small" />}
            label={t('settings.company.title')}
            iconPosition="start"
          />
          <Tab
            icon={<PaymentIcon fontSize="small" />}
            label={t('settings.servicePlan.title')}
            iconPosition="start"
          />
          <Tab
            icon={<WifiIcon fontSize="small" />}
            label={t('hardcoded.pages_settingspage.redes')}
            iconPosition="start"
          />
          <Tab
            icon={<ScheduleIcon fontSize="small" />}
            label="Horarios operativos"
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <CompanyInformationTab
          companyForm={companyForm}
          countries={countries}
          states={states}
          cities={cities}
          companyError={companyError}
          geoError={geoError}
          company={company}
          updateLoading={updateLoading}
          hasChanges={hasChanges}
          onFormChange={handleCompanyFormChange}
          onSave={handleSaveCompany}
          onReset={handleResetForm}
          showDemoCleanup={isArsenalUser && isDemoCompany}
          cleanupLoading={cleanupLoading}
          onCleanupDemo={handleCleanupDemo}
        />
      )}
      {activeTab === 1 && <ServicePlanTab servicePlan={servicePlan} t={t} />}
      {activeTab === 2 && <CompanyNetworksTab />}
      {activeTab === 3 && <OperatingSchedulesTab />}
    </Container>
  );
};

export default SettingsPage;